import React, { useEffect, useState } from "react";
import { Input, Space } from 'antd';
import { useMutation } from "@apollo/client";
import { STAND_UPDATE } from "graphql/mutation/stand-gql";
import Icons from "components/icons";
import Craft from 'components/craft';
import GraphicWarehouse from "components/graphic-warehouse";
import { errorNotification } from "components/request-result";
import { Localize, ModalStandard } from "components/service";
import StandShelfPickerModal from "./stand-shelf-picker-modal";
import './stand-shelf-number-field.scss';

const normalizeShelfNumber = GraphicWarehouse.Helpers.ShelfAvailability.normalizeShelfNumber;

const getMutationErrorMessage = (error) => {
    let errorMessage = 'error occurred';

    if (error?.graphQLErrors && error.graphQLErrors.length > 0)
    {
        const graphQLError = error.graphQLErrors[ 0 ];
        errorMessage = graphQLError?.extensions?.reason || graphQLError?.message || errorMessage;
    } else if (error?.extensions?.reason)
    {
        errorMessage = error.extensions.reason;
    } else if (error?.message)
    {
        errorMessage = error.message;
    }

    return errorMessage;
};

const StandShelfNumberField = ({ id, value, craftData, standId }) => {
    const [ shelfNumber, setShelfNumber ] = useState(normalizeShelfNumber(value));
    const [ error, setError ] = useState(null);
    const [ standUpdate, { loading: saving } ] = useMutation(STAND_UPDATE);

    const effectiveStandId = standId || id;

    const craftCreateUpdate = craftData ?
        Craft.Hooks.createUpdate({ craftID: craftData?.id ? true : false }) :
        { _setCraftCreateUpdate: null };

    const { _setCraftCreateUpdate } = craftCreateUpdate;

    useEffect(() => {
        setShelfNumber(normalizeShelfNumber(value));
    }, [ value ]);

    const updateCraftStatus = (nextShelfNumber) => {
        const hasValidCraft = craftData && _setCraftCreateUpdate;

        if (!hasValidCraft)
        {
            return;
        }

        const isShelfNumberEmpty = !nextShelfNumber || nextShelfNumber.trim() === '';
        let newStatus;

        if (isShelfNumberEmpty)
        {
            newStatus = (craftData.status === 'done' || craftData.status === 'collected') ? 'open' : craftData.status;
        } else
        {
            newStatus = 'done';
        }

        _setCraftCreateUpdate({
            variables: {
                input: {
                    id: craftData?.id ? +craftData.id : undefined,
                    stand_id: +effectiveStandId,
                    status: newStatus,
                }
            }
        });
    };

    const handleShelfSelect = (nextShelfNumber, closeModal) => {
        const normalizedShelfNumber = normalizeShelfNumber(nextShelfNumber);
        const isShelfNumberChanged = normalizedShelfNumber !== normalizeShelfNumber(shelfNumber);

        if (!isShelfNumberChanged)
        {
            closeModal?.();
            return;
        }

        setError(null);

        standUpdate({
            variables: {
                input: {
                    id,
                    shelf_number: normalizedShelfNumber
                }
            }
        }).then(() => {
            setShelfNumber(normalizedShelfNumber);
            updateCraftStatus(normalizedShelfNumber);
            closeModal?.();
        }).catch((error) => {
            errorNotification(error);
            setError(getMutationErrorMessage(error));
        });
    };

    return (
        <div className="stand-shelf-number-field" style={{ position: 'relative' }}>
            <Icons.Shelf className="shelf-icon" />
            <Space
                className="shelf-input-container"
                size={ 0 }>
                <ModalStandard
                    width={ 640 }
                    extraClass="modal-form shelf-picker-modal"
                    disabled={ saving }
                    modalButton={
                        <Input
                            readOnly
                            size="small"
                            className="shelf-picker-trigger"
                            value={ shelfNumber }
                            placeholder={ Localize({ children: "FORMS.Input_Placeholder_ShelfNumber" }).props.children }
                        />
                    }
                >
                    <StandShelfPickerModal
                        onSelect={ handleShelfSelect }
                        currentValue={ shelfNumber }
                        saving={ saving }
                        serverError={ error }
                    />
                </ModalStandard>
            </Space>
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}
        </div>
    );
}

export default StandShelfNumberField;
